import { useEffect, useMemo, useRef } from "react";
import { useFrame, type ThreeEvent } from "@react-three/fiber";
import * as THREE from "three";
import { AU_KM, heliocentricEcliptic, logLerp, norm } from "@solar/ephemeris";
import { useScene } from "../store";
import { bodyLive } from "./bodyState";
import { frame } from "./frame";
import { softPointTexture } from "./materials";

const TAIL_N = 900;

/**
 * 哈雷彗星：细小的彗核 + 背向太阳的彗尾（离子尾粒子云）。
 * 彗尾长度随日距变化：约 3 AU 以内才出现（教学模型模式下按显示单位放大，真实比例按 ~0.1 AU 量级）。
 * 位置来自 Horizons 星历表（packages/ephemeris/data/halley-table.json）。
 */
export function Comet() {
  const hidden = useScene((s) => s.hidden.has("halley"));
  const select = useScene((s) => s.select);
  const g = useRef<THREE.Group>(null);
  const nucleus = useRef<THREE.Mesh>(null);
  const seed = useMemo(() => {
    let s = 99; const rnd = () => (s = (s * 16807) % 2147483647) / 2147483647;
    return Array.from({ length: TAIL_N }, () => ({ t: Math.pow(rnd(), 1.7), a: rnd() * Math.PI * 2, r: Math.sqrt(rnd()) }));
  }, []);
  const geo = useMemo(() => {
    const gm = new THREE.BufferGeometry();
    gm.setAttribute("position", new THREE.BufferAttribute(new Float32Array(TAIL_N * 3), 3));
    gm.setAttribute("color", new THREE.BufferAttribute(new Float32Array(TAIL_N * 3), 3));
    return gm;
  }, []);
  useEffect(() => {
    bodyLive.set("halley", { world: new THREE.Vector3(), north: new THREE.Vector3(0, 1, 0), radius: 0.2, visible: true });
    return () => { bodyLive.delete("halley"); };
  }, []);
  const dir = useMemo(() => new THREE.Vector3(), []), u = useMemo(() => new THREE.Vector3(), []), v = useMemo(() => new THREE.Vector3(), []), tmp = useMemo(() => new THREE.Vector3(), []);

  useFrame(() => {
    const live = bodyLive.get("halley");
    if (!live || !g.current) return;
    const s = useScene.getState();
    live.visible = !hidden;
    g.current.position.copy(live.world).sub(frame.origin);
    if (nucleus.current) nucleus.current.scale.setScalar(Math.max(live.radius, 1e-9));
    // 日距（AU）决定彗尾活跃度
    const rAU = norm(heliocentricEcliptic("halley", new Date(s.timeMs)));
    const act = Math.pow(Math.min(1, Math.max(0, (3 - rAU) / 3)), 1.3);
    const L = logLerp(16, 0.35, frame.m) * act;                       // 显示单位下的彗尾长度
    dir.copy(live.world); if (dir.lengthSq() < 1e-12) dir.set(1, 0, 0); dir.normalize(); // 背向太阳（太阳在绝对原点）
    u.set(0, 1, 0).cross(dir); if (u.lengthSq() < 1e-6) u.set(1, 0, 0).cross(dir); u.normalize(); v.crossVectors(dir, u);
    const pos = geo.attributes.position as THREE.BufferAttribute, col = geo.attributes.color as THREE.BufferAttribute;
    for (let i = 0; i < TAIL_N; i++) {
      const p = seed[i]!, spread = L * 0.07 * p.t * p.r;
      tmp.copy(dir).multiplyScalar(L * p.t).addScaledVector(u, Math.cos(p.a) * spread).addScaledVector(v, Math.sin(p.a) * spread);
      pos.setXYZ(i, tmp.x, tmp.y, tmp.z);
      const f = 0.55 * act * Math.pow(1 - p.t, 1.4) * (1 - 0.5 * p.r);
      col.setXYZ(i, 0.55 * f, 0.78 * f, 1.0 * f);
    }
    pos.needsUpdate = true; col.needsUpdate = true; geo.boundingSphere = null;
  });
  const click = (e: ThreeEvent<MouseEvent>) => { if (e.delta > 5) return; e.stopPropagation(); select("halley"); };
  if (hidden) return null;
  return (
    <group ref={g}>
      <mesh ref={nucleus} onClick={click} onPointerOver={() => { document.body.style.cursor = "pointer"; }} onPointerOut={() => { document.body.style.cursor = ""; }}>
        <sphereGeometry args={[1, 16, 12]} />
        <meshStandardMaterial color="#9a9488" roughness={1} />
      </mesh>
      {/* 彗核光点：模型模式下彗核太小，用一个不随距离缩放的亮点保证可见 */}
      <points raycast={() => null}>
        <bufferGeometry><bufferAttribute attach="attributes-position" args={[new Float32Array([0, 0, 0]), 3]} /></bufferGeometry>
        <pointsMaterial map={softPointTexture()} size={9} sizeAttenuation={false} color="#e8f2ff" transparent blending={THREE.AdditiveBlending} depthWrite={false} />
      </points>
      <points geometry={geo} raycast={() => null} frustumCulled={false}>
        <pointsMaterial map={softPointTexture()} size={6} sizeAttenuation={false} vertexColors transparent blending={THREE.AdditiveBlending} depthWrite={false} />
      </points>
    </group>
  );
}
void AU_KM;
